import { Button, Form } from "react-bootstrap";
import React, { useState } from "react";
import SelectPractices from "../../../SelectPractices";
import { Formik } from "formik";
import Select from "react-select";
import useSummaryReportForm from "./useSummaryReportForm";

const SELECTION_MODES = {
  PRACTICE: "practice",
  CLIENT: "client",
};

const switchStyles = {
  track: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: 38,
    padding: 3,
    border: "1px solid #ced4da",
    borderRadius: 19,
    backgroundColor: "#f8f9fa",
    cursor: "pointer",
    userSelect: "none",
  },
  option: {
    position: "relative",
    zIndex: 1,
    flex: 1,
    textAlign: "center",
    fontSize: 13,
    fontWeight: 600,
    transition: "color 180ms ease",
  },
  thumb: {
    position: "absolute",
    top: 3,
    bottom: 3,
    left: 3,
    width: "calc(50% - 3px)",
    borderRadius: 16,
    backgroundColor: "#007bff",
    boxShadow: "0 2px 8px rgba(0, 123, 255, 0.28)",
    transition: "transform 180ms ease",
  },
};

const formStyles = {
  reportByField: {
    width: "100%",
    maxWidth: 260,
    marginBottom: 28,
  },
  fieldsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "26px 30px",
    alignItems: "end",
  },
  field: {
    minWidth: 0,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 28,
  },
  submitButton: {
    width: "min(100%, 250px)",
  },
};

const SummaryReportForm = ({ GetSummaryReport, clients = [], practices = [] }) => {
  const { data, selectRef, clientSelectRef, clearSelect } =
    useSummaryReportForm();
  const [selectionMode, setSelectionMode] = useState(SELECTION_MODES.PRACTICE);

  const getSelectedField = () =>
    selectionMode === SELECTION_MODES.PRACTICE ? "practiceId" : "clientId";

  const buildReportValues = (values) => {
    const selectedField = getSelectedField();
    const reportValues = {
      startDate: values.startDate,
      endDate: values.endDate,
      [selectedField]: values[selectedField],
    };

    if (selectionMode === SELECTION_MODES.PRACTICE) {
      const selectedPractice = practices.find(
        (practice) => practice._id === values.practiceId,
      );

      reportValues.clientId = selectedPractice?.clientId || "";
    }

    return reportValues;
  };

  const canGenerateReport = (values) =>
    Boolean(values[getSelectedField()] && values.startDate && values.endDate);

  const changeSelectionMode = (mode, props) => {
    setSelectionMode(mode);

    if (mode === SELECTION_MODES.PRACTICE) {
      props.setFieldValue("clientId", "");
      clientSelectRef.current?.select?.setValue(null);
      return;
    }

    props.setFieldValue("practiceId", "");
    selectRef.current?.select?.setValue({
      label: "Select Practice",
      value: "-1",
    });
  };

  return (
    <>
      <Formik
        initialValues={data}
        enableReinitialize={true}
        onSubmit={(values, {resetForm}) => {
          GetSummaryReport(buildReportValues(values))
          resetForm()
          clearSelect()
        }}
      >
        {props => (
          <form onSubmit={props.handleSubmit}>
            <div>
              <div style={formStyles.reportByField}>
                <Form.Group>
                  <Form.Label>Report By</Form.Label>
                  <div
                    role="switch"
                    aria-checked={selectionMode === SELECTION_MODES.CLIENT}
                    tabIndex={0}
                    style={switchStyles.track}
                    onClick={() => {
                      changeSelectionMode(
                        selectionMode === SELECTION_MODES.PRACTICE
                          ? SELECTION_MODES.CLIENT
                          : SELECTION_MODES.PRACTICE,
                        props,
                      );
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      changeSelectionMode(
                        selectionMode === SELECTION_MODES.PRACTICE
                          ? SELECTION_MODES.CLIENT
                          : SELECTION_MODES.PRACTICE,
                        props,
                      );
                    }}
                  >
                    <span
                      style={{
                        ...switchStyles.thumb,
                        transform:
                          selectionMode === SELECTION_MODES.CLIENT
                            ? "translateX(100%)"
                            : "translateX(0)",
                      }}
                    />
                    <span
                      style={{
                        ...switchStyles.option,
                        color:
                          selectionMode === SELECTION_MODES.PRACTICE
                            ? "#fff"
                            : "#495057",
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        changeSelectionMode(SELECTION_MODES.PRACTICE, props);
                      }}
                    >
                      Practice
                    </span>
                    <span
                      style={{
                        ...switchStyles.option,
                        color:
                          selectionMode === SELECTION_MODES.CLIENT
                            ? "#fff"
                            : "#495057",
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        changeSelectionMode(SELECTION_MODES.CLIENT, props);
                      }}
                    >
                      Client
                    </span>
                  </div>
                </Form.Group>
              </div>

              <div style={formStyles.fieldsGrid}>
                <div style={formStyles.field}>
                  <Form.Group>
                    {selectionMode === SELECTION_MODES.PRACTICE ? (
                      <SelectPractices
                        selectRef={selectRef}
                        selected={props.values.practiceId}
                        onChange={({value}) => {
                          props.setFieldValue('practiceId', value)
                        }}
                      />
                    ) : (
                      <>
                        <Form.Label htmlFor="clientId">Client</Form.Label>
                        <Select
                          name="clientId"
                          options={clients}
                          ref={clientSelectRef}
                          onChange={(option) => {
                            props.setFieldValue("clientId", option?.value || "");
                          }}
                        />
                      </>
                    )}
                  </Form.Group>
                </div>

                <div style={formStyles.field}>
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      id="startDate"
                      name="startDate"
                      value={props.values.startDate}
                      required
                      onChange={event => {
                        props.setFieldValue('startDate', event.target.value)
                      }
                      }
                    >

                    </Form.Control>
                  </Form.Group>
                </div>

                <div style={formStyles.field}>
                  <Form.Group>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      id="endDate"
                      name="endDate"
                      value={props.values.endDate}
                      required
                      onChange={event => {
                        props.setFieldValue('endDate', event.target.value)
                      }
                      }
                    >
                    </Form.Control>
                  </Form.Group>
                </div>
              </div>

              <div style={formStyles.actions}>
                <Button
                  type='submit'
                  className='btn btn-primary'
                  style={formStyles.submitButton}
                  disabled={!canGenerateReport(props.values)}
                >
                  Generate
                </Button>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </>
  )
}

export default SummaryReportForm
